<template>
 <ModalConsulte ref="modalRef" :hide-footer="false" modalWidth="700px !important">
  <template #header>
   <h4 class="modal-title">Devolução de Empréstimo</h4>
  </template>
  <template #content>
   <div class="row g-3">
    <div class="col-md-4">
     <label class="form-label" for="campo-dataDevolucao-970e">Data de Devolução</label>
     <input v-model="form.dataDevolucao" type="date" class="form-control" @change="atualizarMulta" id="campo-dataDevolucao-970e" />
    </div>
    <div v-if="mostrarCamposMulta" class="col-md-4">
     <label class="form-label" for="campo-text-970e">Multa</label>
     <input :value="form.multa.toFixed(2)" type="text" class="form-control" readonly id="campo-text-970e" />
    </div>
    <div v-if="mostrarCamposMulta" class="col-md-2 d-flex align-items-end">
     <div class="form-check mb-2">
      <input id="multaPaga" v-model="multaPagaMarcada" class="form-check-input" type="checkbox" @change="onChangeMultaPaga" />
      <label class="form-check-label" for="multaPaga">Multa Paga</label>
     </div>
    </div>
    <div v-if="podeDispensarMulta" class="col-md-2 d-flex align-items-end">
     <div class="form-check mb-2">
      <input
       id="dispensaMulta"
       v-model="dispensaMultaMarcada"
       class="form-check-input"
       type="checkbox"
       @change="onChangeDispensaMulta"
      />
      <label class="form-check-label" for="dispensaMulta">Dispensa de Multa</label>
     </div>
    </div>
    <div v-if="mostrarDropDownMotivoDispensa" class="col-md-12">
     <DropDownListBibMotivoDispensaMulta v-model="form.bibMotivoDispensaId" />
    </div>
   </div>
   <div v-if="mensagemErro" class="alert alert-danger mt-3 mb-0">
    {{ mensagemErro }}
   </div>
  </template>
  <template #actions>
   <button type="button" class="btn btn-light" @click="close">Cancelar</button>
   <button v-if="permissaoTela.alterar" type="button" class="btn btn-primary" :disabled="salvando" @click="salvar">
    {{ salvando ? "Salvando..." : "OK" }}
   </button>
  </template>
 </ModalConsulte>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import ModalConsulte from "@/components/modal/ModalConsulte.vue";
import DropDownListBibMotivoDispensaMulta from "@/components/formulario/Biblioteca/DropDownListBibMotivoDispensaMulta.vue";
import { BibEmprestimoModel } from "@/models/biblioteca/bibemprestimo/bibemprestimo.model";
import BibEmprestimoService from "@/services/biblioteca/bibemprestimo/bibemprestimo.service";
import BibTipoUsuarioService from "@/services/biblioteca/bibtipousuario/bibtipousuario.service";
import { calculateLateFee, formatDateInput } from "./utils";
import {
 PERMISSAO_TELA_INJECT_KEY,
 permissaoTelaLiberada,
} from "@/composables/usePermissaoTela/usePermissaoTela";

export default defineComponent({
 name: "ModalDevolucaoEmprestimo",
 components: { ModalConsulte, DropDownListBibMotivoDispensaMulta },
 emits: ["saved"],
 inject: {
  // Permissão da tela de empréstimo que abriu o modal.
  permissaoTela: {
   from: PERMISSAO_TELA_INJECT_KEY,
   default: () => permissaoTelaLiberada(),
  },
 },
 data() {
  return {
   salvando: false,
   mensagemErro: "",
   multaPagaMarcada: false,
   dispensaMultaMarcada: false,
   model: new BibEmprestimoModel() as BibEmprestimoModel,
   form: {
    dataDevolucao: "",
    multa: 0,
    bibMotivoDispensaId: null as number | null,
   },
  };
 },
 computed: {
  mostrarCamposMulta(): boolean {
   return this.form.multa > 0;
  },
  dataLimiteUltrapassada(): boolean {
   const dataDevolucao = this.toDateOnly(this.form.dataDevolucao);
   const dataLimite = this.toDateOnly(this.model.dataVencimento);
   if (!dataDevolucao || !dataLimite) {
    return false;
   }

   return dataDevolucao > dataLimite;
  },
  podeDispensarMulta(): boolean {
   return this.mostrarCamposMulta && !this.multaPagaMarcada && this.dataLimiteUltrapassada;
  },
  mostrarDropDownMotivoDispensa(): boolean {
   return this.podeDispensarMulta && this.dispensaMultaMarcada;
  },
 },
 methods: {
  toDateOnly(value?: Date | string | null): Date | null {
   if (!value) return null;

   if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split("-").map(Number);
    return new Date(year, month - 1, day, 0, 0, 0, 0);
   }

   const date = value instanceof Date ? new Date(value) : new Date(value);
   if (Number.isNaN(date.getTime())) {
    return null;
   }

   date.setHours(0, 0, 0, 0);
   return date;
  },
  validarEstadoDispensaMulta() {
   if (!this.podeDispensarMulta) {
    this.dispensaMultaMarcada = false;
    this.form.bibMotivoDispensaId = null;
   }
  },
  async open(row: BibEmprestimoModel) {
   this.model = { ...new BibEmprestimoModel(), ...row };
   const bibTipoUsuarioIdRota = Number(this.$route.params.bibTipoUsuarioId || 0);
   this.model.bibTipoUsuarioId = Number(this.model.bibTipoUsuarioId || bibTipoUsuarioIdRota || 0);
   await this.carregarParametrosTipoUsuario(this.model.bibTipoUsuarioId);
   this.form.dataDevolucao = formatDateInput(new Date());
   this.form.multa = 0;
   this.form.bibMotivoDispensaId = row.bibMotivoDispensaId ?? null;
   this.multaPagaMarcada = Number(row.multaPaga) === 1;
   this.dispensaMultaMarcada = Number(row.dispensaMulta) === 1;
   this.mensagemErro = "";
   this.atualizarMulta();
   this.validarEstadoDispensaMulta();
   (this.$refs.modalRef as any)?.open();
  },
  close() {
   (this.$refs.modalRef as any)?.close();
  },
  getMoraAtraso() {
   const moraAtraso = Number(this.model.bibTipoUsuario?.moraAtraso ?? this.model.moraAtraso ?? 0);
   return Number.isFinite(moraAtraso) && moraAtraso > 0 ? moraAtraso : 0;
  },
  async carregarParametrosTipoUsuario(bibTipoUsuarioIdRaw?: number | string | null) {
   const bibTipoUsuarioId = Number(bibTipoUsuarioIdRaw ?? this.model.bibTipoUsuarioId ?? 0);
   if (!bibTipoUsuarioId) {
    this.model.bibTipoUsuario = null;
    this.model.prazoEmprestimo = 0;
    this.model.moraAtraso = 0;
    return;
   }

   if (Number(this.model.bibTipoUsuario?.bibTipoUsuarioId || 0) === bibTipoUsuarioId) {
    return;
   }

   try {
    const tipoUsuario = await BibTipoUsuarioService.get(bibTipoUsuarioId);
    this.model.bibTipoUsuario = tipoUsuario;
    this.model.prazoEmprestimo = Number(tipoUsuario?.prazoEmprestimo ?? 0);
    this.model.moraAtraso = Number(tipoUsuario?.moraAtraso ?? 0);
   } catch (error) {
    this.model.bibTipoUsuario = null;
    this.model.prazoEmprestimo = 0;
    this.model.moraAtraso = 0;
    console.error("Erro ao carregar parâmetros do tipo de usuário:", error);
   }
  },
  atualizarMulta() {
   this.form.multa = calculateLateFee(
    this.form.dataDevolucao,
    this.model.dataVencimento,
    this.getMoraAtraso()
   );
   if (!this.mostrarCamposMulta) {
    this.multaPagaMarcada = false;
   }
   this.validarEstadoDispensaMulta();
  },
  onChangeMultaPaga() {
   this.validarEstadoDispensaMulta();
  },
  onChangeDispensaMulta() {
   if (!this.dispensaMultaMarcada) {
    this.form.bibMotivoDispensaId = null;
   }
  },
  async salvar() {
   this.mensagemErro = "";

   if (!this.form.dataDevolucao) {
    this.mensagemErro = "Informe a data de devolução.";
    return;
   }

   if (this.dispensaMultaMarcada && !Number(this.form.bibMotivoDispensaId || 0)) {
    this.mensagemErro = "Informe o motivo de dispensa da multa.";
    return;
   }

   const payload: BibEmprestimoModel = {
    ...this.model,
    dataDevolucao: this.form.dataDevolucao as unknown as Date,
    multa: this.form.multa,
    devolvido: 1,
    multaPaga: this.multaPagaMarcada ? 1 : 0,
    dispensaMulta: this.dispensaMultaMarcada ? 1 : 0,
    bibMotivoDispensaId: this.dispensaMultaMarcada ? Number(this.form.bibMotivoDispensaId || 0) : 0,
    situacao: 1,
   };

   this.salvando = true;
   try {
    await BibEmprestimoService.inserirAtualizar(payload);
    this.close();
    this.$emit("saved");
   } catch (error) {
    this.mensagemErro = "Ocorreu um erro ao salvar a devolução.";
    console.error("Erro ao salvar devolução:", error);
   } finally {
    this.salvando = false;
   }
  },
 },
});
</script>
